// ---------------------------------------------------------------------------
// Ce que chacun veut entendre et lire
//
// Sur le serveur Discord, des rôles disent déjà comment on regarde : la version
// japonaise ou la française, les sous-titres en français, en anglais ou pas du
// tout. Ces rôles existent pour les notifications ; ils servent ici à régler le
// lecteur tout seul.
//
// L'idée tient en une phrase : personne ne devrait avoir à ouvrir les réglages
// pour dire une deuxième fois ce qu'il a déjà dit en cliquant sur un bouton à
// son arrivée.
//
// DEUX CHOIX INDÉPENDANTS, et c'est important : la version audio et les
// sous-titres ne se déduisent pas l'un de l'autre. On peut vouloir la version
// japonaise sans sous-titres, ou la version française avec des sous-titres
// français. Les mélanger obligerait à inventer des combinaisons que personne
// n'a demandées.
//
// Ce module ne connaît ni Discord ni le lecteur : il traduit une liste de rôles
// en une paire de préférences, rien de plus. C'est ce qui le rend éprouvable.
// ---------------------------------------------------------------------------

/** Les deux familles de choix, et ce que chacune accepte. */
export const FAMILLES = {
  audio: {
    nom: 'Version',
    choix: [
      { cle: 'vo', nom: 'Japonaise', role: 'audioVo' },
      { cle: 'vf', nom: 'Française', role: 'audioVf' },
    ],
  },
  soustitres: {
    nom: 'Sous-titres',
    choix: [
      { cle: 'fr', nom: 'Français', role: 'stFr' },
      { cle: 'en', nom: 'Anglais', role: 'stEn' },
      { cle: 'aucun', nom: 'Aucun', role: 'stAucun' },
    ],
  },
};

/** Tous les champs de la table de correspondance, à plat. */
export const CHAMPS = Object.values(FAMILLES).flatMap((f) => f.choix.map((c) => c.role));

export const TABLE_VIDE = Object.freeze(Object.fromEntries(CHAMPS.map((c) => [c, ''])));

/**
 * Ce que les rôles d'un membre disent de ses préférences.
 *
 * @param {string[]} roles  identifiants des rôles portés par le membre
 * @param {object} table    correspondance rôle → choix, réglée dans l'admin
 * @returns {{audio: string|null, soustitres: string|null}}
 *
 * Un membre qui porte deux rôles de la même famille — la version japonaise ET
 * la française — n'a pas dit ce qu'il voulait : on ne tranche pas à sa place et
 * on rend `null`, ce qui laisse ses réglages personnels décider. Deviner ici
 * donnerait un lecteur qui change d'avis sans qu'on comprenne pourquoi.
 */
export function goutDesRoles(roles, table) {
  const siens = new Set((roles ?? []).map(String));
  const resultat = {};

  for (const [famille, def] of Object.entries(FAMILLES)) {
    const trouves = def.choix.filter((c) => {
      const id = table?.[c.role];
      return id && siens.has(String(id));
    });
    resultat[famille] = trouves.length === 1 ? trouves[0].cle : null;
  }

  return resultat;
}

/**
 * Devine la correspondance à partir des NOMS des rôles du serveur.
 *
 * Sert une seule fois, pour proposer un réglage plausible plutôt qu'un
 * formulaire vide : l'exploitant vérifie et corrige. On ne devine jamais par
 * -dessus un choix déjà enregistré.
 */
export function devinerTable(roles) {
  const table = { ...TABLE_VIDE };
  const propre = (n) => String(n ?? '').toLowerCase().replace(/[^a-z]/g, '');

  for (const r of roles ?? []) {
    const n = propre(r.nom ?? r.name);
    const estSousTitre = /^st/.test(n);

    if (estSousTitre) {
      // « ST NO » d'abord : « no » contient un « n » que d'autres règles
      // pourraient attraper, et l'absence de sous-titres est le cas le plus
      // facile à confondre.
      if (/no|aucun|sans|off/.test(n)) table.stAucun ||= r.id;
      else if (/fr|vf/.test(n)) table.stFr ||= r.id;
      else if (/en|ang|vj|vo|jap/.test(n)) table.stEn ||= r.id;
      continue;
    }

    if (/jap|vo$|vost/.test(n)) table.audioVo ||= r.id;
    else if (/fran|vf$/.test(n)) table.audioVf ||= r.id;
  }

  return table;
}

/**
 * Ce que la séance doit servir à ce spectateur : la langue de la piste audio.
 *
 * Rendu séparément parce que c'est la seule préférence qui décide du FICHIER
 * ouvert — les sous-titres, eux, se règlent après coup sans changer de source.
 */
export const langueDe = (gout) => (gout?.audio === 'vf' ? 'vf' : gout?.audio === 'vo' ? 'vo' : null);

// ---------------------------------------------------------------------------
// D'un goût aux réglages du lecteur
//
// Cette traduction vivait dans le client, en dur dans `appliquerGout` : la
// table des ordres de langues (« fre, fra, fr »), et le fait qu'une version
// japonaise s'écrive `vostfr` du côté des réglages alors qu'elle s'appelle
// `vo` ici. Tant qu'un seul fichier s'en servait, c'était acceptable.
//
// Depuis que les réglages se conservent sur le compte, un deuxième fichier a
// besoin de la MÊME table : le stockage doit savoir quelles valeurs il a le
// droit d'accepter pour `langue`, `st-langues` et `st-sans`, sinon il les
// devine — et deux devinettes finissent toujours par diverger. Elles sont donc
// écrites ici une fois, à l'endroit qui définit déjà le vocabulaire.
//
// Un mot sur `vostfr` : le nom est trompeur (il ne dit rien des sous-titres,
// qui sont un choix indépendant), mais c'est la valeur déjà enregistrée dans
// les navigateurs de tout le monde. La renommer effacerait leur réglage.
// ---------------------------------------------------------------------------

/** Les trois valeurs que le lecteur accepte pour la piste audio. */
export const VALEURS_LANGUE = Object.freeze(['auto', 'vf', 'vostfr']);

/** Comment un choix de la famille « audio » s'écrit dans les réglages. */
export const LANGUE_DU_GOUT = Object.freeze({ vo: 'vostfr', vf: 'vf' });

/**
 * L'ordre de langues que vaut chaque choix de sous-titres.
 *
 * Un fichier annonce la même langue de trois façons (`fre`, `fra`, `fr`) : on
 * enregistre donc une liste ordonnée, pas un nom. « Aucun » n'est pas une
 * langue — la liste vide ne suffirait pas, elle ferait retomber sur la
 * première piste venue —, d'où le drapeau séparé plus bas.
 */
export const ORDRE_SOUSTITRES = Object.freeze({ fr: 'fre, fra, fr', en: 'eng, en', aucun: '' });

/**
 * Les seuls réglages qu'un rôle peut poser.
 *
 * La liste est ici plutôt que recopiée chez ceux qui s'en servent : le lecteur
 * la consulte pour savoir ce qu'il a le droit de marquer « réglé à la main »,
 * et le stockage des réglages du compte pour savoir ce qu'il a le droit
 * d'accepter dans cette marque. Deux copies auraient dérivé au premier réglage
 * ajouté, et le symptôme aurait été une marque refusée sans explication —
 * donc des rôles qui regagnent sur un choix personnel.
 */
export const CLES_DU_GOUT = Object.freeze(['langue', 'st-langues', 'st-sans']);

/**
 * Les réglages que ce goût pose, sous la forme exacte attendue par le lecteur.
 * Les clés absentes ne sont pas posées : ne rien savoir n'est pas la même
 * chose que vouloir la valeur par défaut.
 *
 * @param {{audio: string|null, soustitres: string|null}} gout
 * @returns {Record<string, string>}
 */
export function reglagesDuGout(gout) {
  const sortie = {};
  const langue = LANGUE_DU_GOUT[gout?.audio];
  if (langue) sortie.langue = langue;

  const st = gout?.soustitres;
  if (st && st in ORDRE_SOUSTITRES) {
    sortie['st-langues'] = ORDRE_SOUSTITRES[st];
    sortie['st-sans'] = st === 'aucun' ? 'oui' : 'non';
  }
  return sortie;
}
